// RÉPLICA DE PARIDAD admin↔web (regla PointsHelper): cambiar este módulo exige
// cambiar su gemelo en krispis-web (src/api/PosSessions/PosSession.ts).
//
// Sesión VIVA de una caja (PLAN-ESPEJO-POS-Y-PROPINA, D1): un doc por
// colaborador+sucursal (id determinista `{branchOfficeId}_{collaboratorId}`)
// que el POS 2.0 sobrescribe con cada cambio del carrito y la pantalla espejo
// (/display de krispis-web) lee con onSnapshot directo al doc — sin queries,
// sin índices y sin tocar la colección `orders` (la orden real conserva su
// ciclo de vida intacto). El doc se sobrescribe en el lugar: nunca crece.
//
// Propiedad de escritura: el POS es dueño de TODO el doc, salvo dos bloques
// que puede escribir el espejo — `tip` solo durante stage 'tip' y
// `customerRequest` solo durante stage 'building'. El cajero siempre manda:
// confirma la propina antes de cobrar y decide si aplica el customerRequest.
//
// ⚠️ El espejo es una pantalla pública y no hay firestore.rules: aquí NUNCA
// van datos personales del cliente más allá del nombre para saludar, y NUNCA
// su saldo de puntos.
import { firestore } from '@src/api/Firebase/Firebase';
import { collection, Timestamp } from 'firebase/firestore';
import { OrderLineV2 } from '@api/Orders/OrderLine';

// 'tip' y 'charging' son LEGACY: el paso de propina dejó de ser una etapa
// (vive inline en el resumen del espejo). Se conservan en el tipo porque puede
// haber docs viejos en Firestore con ese valor.
export type PosSessionStage = 'idle' | 'building' | 'tip' | 'charging' | 'done';

// La propina que el cliente eligió EN SU PANTALLA (rediseño 2026-08-26, 4ª
// ronda): ya no hay un "paso de propina" que el cajero dispare — el selector
// vive SIEMPRE al final del resumen del espejo y el cliente cierra su parte con
// "Confirmar orden". Por eso este bloque es también la señal de que el cliente
// confirmó: `status !== 'pending'` = ya decidió (con propina o sin ella).
export interface PosSessionTip {
    status: 'pending' | 'selected' | 'skipped';
    amount: number;
    // % elegido; null = monto custom tecleado
    percent?: number | null;
    source?: 'customer' | 'pos';
    // Total sobre el que se calculó. Si el pedido cambia después de confirmar,
    // el % quedaría sobre una cuenta vieja: el espejo lo detecta comparando
    // contra `totals.total` y pide confirmar de nuevo.
    baseTotal?: number;
}

// D7: el cliente se identificó desde el espejo. Dos variantes:
// - EXISTENTE (isNew false): trae uid + name; el POS lo asigna solo (si su
//   carrito aún no tiene cliente con uid) y deja el bloque en null.
// - NUEVA (isNew true): trae SOLO phone — la pantalla del cliente no tiene
//   teclado físico, así que el cliente captura su número en el espejo y el
//   POS abre el alta con el teléfono precargado para que el cajero complete
//   el nombre (ajuste de QA 2026-08-26).
export interface PosSessionCustomerRequest {
    uid?: string;
    name?: string; // nombre de pila + inicial — nunca el nombre completo
    phone?: string; // solo en la variante nueva (10 dígitos normalizados)
    isNew: boolean;
    requestedAt: Timestamp | Date;
}

export interface PosSessionTotals {
    subtotal: number;
    promotion: number;
    // Envío: suma al total (raro en mostrador, pero el resumen lo espeja igual)
    shippingCost?: number;
    pointsAmount: number;
    // Total a pagar YA sin promoción ni puntos — es la BASE de la propina (D2):
    // el espejo nunca calcula el % sobre el subtotal.
    total: number;
    // Cobro en curso (QA 2026-08-26, 5ª ronda): el espejo pinta el MISMO
    // resumen que el cajero, en el mismo orden — propina y pagos registrados.
    tipAmount?: number;
    creditCardAmount?: number;
    bankTransferAmount?: number;
    // Efectivo capturado en parcialidades (pago libre)
    cashRegisteredAmount?: number;
    // total + propina − tarjeta − transferencia (lo que se persiste como
    // `cashAmount` de la orden). El espejo NO lo calcula: lo escribe el POS.
    cashAmount?: number;
    // Lo que FALTA por cobrar (ya descontando el efectivo en parcialidades).
    // Va escrito y no derivado: el espejo solo pinta.
    remaining?: number;
}

export interface PosSessionCart {
    products: OrderLineV2[]; // sin el id local del carrito
    customerName?: string; // nombre de pila + inicial si es cliente registrado (pantalla pública)
    // El pedido ya tiene cliente identificado (con uid) — apaga el CTA de
    // identificación del espejo (D7)
    customerHasAccount?: boolean;
    note?: string;
}

export interface PosSessionLastOrder {
    id: string;
    shortId: string;
    status: string; // WaitingForPayment → el espejo muestra "pendiente de pago"
    total: number;
    tipAmount: number;
}

export interface PosSession {
    id?: string;
    branchOfficeId: string;
    collaboratorId: string;
    collaboratorName: string; // snapshot — el espejo no lee `users`
    stage: PosSessionStage;
    cart?: PosSessionCart | null;
    totals?: PosSessionTotals | null;
    tip?: PosSessionTip | null;
    customerRequest?: PosSessionCustomerRequest | null;
    // El cajero pidió que el cliente se identifique EN SU PANTALLA: el espejo
    // abre su teclado de teléfono cada vez que este valor CAMBIA (por eso es
    // timestamp y no bool — permite re-pedirlo). Lo escribe solo el POS.
    identifyRequestedAt?: Timestamp | Date | null;
    lastOrder?: PosSessionLastOrder | null;
    updatedAt: Timestamp | Date;
    appVersion?: string; // diagnóstico de tablets con build viejo
}

export const getPosSessionId = (branchOfficeId: string, collaboratorId: string): string =>
    `${branchOfficeId}_${collaboratorId}`;

export const POS_SESSIONS_COLLECTION = 'pos-sessions';

export const posSessionsRef = collection(firestore, POS_SESSIONS_COLLECTION);
